import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import { CalendarClock } from 'lucide-react';
import CurrencyInput from '../../../../../components/CurrencyInput';
import CompanySelect from '../../../FashionOrders/SelectItem/CompanySelect';

import { OrderSchemaType } from '../order.schema';
import { computeDelivery, buildHolidayMessage } from '../utils/businessDays';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';

import { FormGroup, Label, Input } from '../style';

import { HolidayChip, DerivedHint } from './ShippingSection.style';

export const CarrierField: React.FC = () => {
  const { watch, setValue } = useFormContext<OrderSchemaType>();
  const carrier = watch('carrier');

  return (
    <FormGroup>
      <CompanySelect
        label="Transportadora"
        apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`}
        displayField="companyName"
        queryParams={{ companyType: 'Transportadora', excludeMainCompany: 'true' }}
        value={carrier}
        onChange={(v) => setValue('carrier', v, { shouldDirty: true })}
      />
    </FormGroup>
  );
};

export const ShippingValueField: React.FC = () => {
  const { control, formState: { errors } } = useFormContext<OrderSchemaType>();

  return (
    <FormGroup>
      <Label htmlFor="shippingValue">Valor do Frete</Label>
      <Controller
        name="shippingValue"
        control={control}
        render={({ field }) => (
          <CurrencyInput
            id="shippingValue"
            name={field.name}
            value={field.value || 0}
            onValueChange={(value, name, values) => {
              field.onChange(values?.float || 0);
            }}
            placeholder="R$ 0,00"
            hasError={!!get(errors, 'shippingValue')}
          />
        )}
      />
    </FormGroup>
  );
};

export const ShippingDeadlineField: React.FC = () => {
  const [holidayNotice, setHolidayNotice] = useState<string | null>(null);
  const { setFeedback } = useContext(FeedbackContext);
  const previewFailedRef = useRef(false);

  const { control, register, setValue, getValues, formState: { errors } } = useFormContext<OrderSchemaType>();

  const applyBusinessDays = useCallback(async (days: number) => {
    if (isNaN(days) || days < 0) {
      setHolidayNotice(null);
      return;
    }
    const computation = await computeDelivery(getValues('orderDate'), days);
    if (computation.failed) {
      if (!previewFailedRef.current) {
        previewFailedRef.current = true;
        setFeedback({ message: 'Não foi possível calcular a previsão de entrega. Informe a data manualmente.', isOpen: true, statusOk: false });
      }
      setHolidayNotice(null);
      return;
    }
    previewFailedRef.current = false;
    if (computation.date) {
      const previousDelivery = getValues('deliveryDate') || '';
      const currentBilling = getValues('billingDate') || '';
      setValue('deliveryDate', computation.date, { shouldDirty: true, shouldValidate: false });
      if (!currentBilling || currentBilling === previousDelivery) {
        setValue('billingDate', computation.date, { shouldDirty: true, shouldValidate: false });
      }
    }
    setHolidayNotice(computation.skippedHolidays.length > 0 ? buildHolidayMessage(computation) : null);
  }, [getValues, setValue, setFeedback]);

  const businessDaysRegistration = register('deliveryBusinessDays', {
    setValueAs: (value) => value === '' || value === null ? undefined : Number(value),
  });
  const handleBusinessDaysChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    businessDaysRegistration.onChange(e);
    const raw = e.target.value;
    if (raw === '') {
      setHolidayNotice(null);
      return;
    }
    await applyBusinessDays(Number(raw));
  };

  const itemsWatch = useWatch({ control, name: 'items' }) as OrderSchemaType['items'] | undefined;
  const maxItemsBusinessDays = React.useMemo(() => {
    let max: number | undefined;
    (itemsWatch || []).forEach(item => {
      const raw = (item as any)?.deliveryBusinessDays;
      if (raw === undefined || raw === null || raw === '') return;
      const days = Number(raw);
      if (isNaN(days) || days <= 0) return;
      if (max === undefined || days > max) max = days;
    });
    return max;
  }, [itemsWatch]);

  const isDerivedFromItems = maxItemsBusinessDays !== undefined;

  useEffect(() => {
    if (maxItemsBusinessDays === undefined) return;
    if (Number(getValues('deliveryBusinessDays')) === maxItemsBusinessDays) return;
    setValue('deliveryBusinessDays', maxItemsBusinessDays, { shouldDirty: true, shouldValidate: false });
    applyBusinessDays(maxItemsBusinessDays);
  }, [maxItemsBusinessDays, getValues, setValue, applyBusinessDays]);

  return (
    <FormGroup>
      <Label htmlFor="deliveryBusinessDays">Prazo de entrega (dias úteis)</Label>
      <Input
        id="deliveryBusinessDays"
        type="number"
        min={0}
        {...businessDaysRegistration}
        onChange={handleBusinessDaysChange}
        placeholder={isDerivedFromItems ? 'Derivado dos itens' : 'Dias úteis'}
        disabled={isDerivedFromItems}
        hasError={!!get(errors, 'deliveryBusinessDays')}
      />
      {isDerivedFromItems && <DerivedHint>Maior prazo entre os itens</DerivedHint>}
      {holidayNotice && (
        <HolidayChip>
          <CalendarClock size={14} />
          <span>{holidayNotice}</span>
        </HolidayChip>
      )}
    </FormGroup>
  );
};
